import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "../context/ThemeContext";
import { useMediaServerConnection } from "@/hooks/useMediaServerConnection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Plane, 
  RefreshCw, 
  ArrowLeft, 
  Gauge, 
  Navigation, 
  Mountain,
  Radio,
  Clock,
  MapPin,
  Wifi,
  WifiOff,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Video,
  X,
  Shield,
  Sun,
  Moon
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// White/silver NSAS logo
const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

// Inject keyframes for 3D spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin3d {
    0% { transform: rotateY(0deg) rotateX(5deg) scale(1); }
    12.5% { transform: rotateY(45deg) rotateX(0deg) scale(0.9); }
    25% { transform: rotateY(90deg) rotateX(-5deg) scale(0.75); }
    37.5% { transform: rotateY(135deg) rotateX(0deg) scale(0.9); }
    50% { transform: rotateY(180deg) rotateX(5deg) scale(1); }
    62.5% { transform: rotateY(225deg) rotateX(0deg) scale(0.9); }
    75% { transform: rotateY(270deg) rotateX(-5deg) scale(0.75); }
    87.5% { transform: rotateY(315deg) rotateX(0deg) scale(0.9); }
    100% { transform: rotateY(360deg) rotateX(5deg) scale(1); }
  }
`;
if (!document.querySelector('#tracker-spinner-styles')) {
  styleSheet.id = 'tracker-spinner-styles';
  document.head.appendChild(styleSheet);
}

// Aircraft icon SVG with callsign label
const createAircraftIcon = (color = "#FF6B00", heading = 0, callsign = "") => {
  const svgIcon = `
    <div style="position: relative; width: 40px; height: 40px;">
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style="transform: rotate(${heading}deg)">
        <g transform="translate(20, 20)">
          <path d="M0,-15 L4,-5 L15,5 L15,8 L4,5 L4,12 L8,15 L8,18 L0,16 L-8,18 L-8,15 L-4,12 L-4,5 L-15,8 L-15,5 L-4,-5 Z" 
                fill="${color}" 
                stroke="#000" 
                stroke-width="1"/>
        </g>
      </svg>
      ${callsign ? `<div style="position: absolute; top: 42px; left: 50%; transform: translateX(-50%); white-space: nowrap; background: rgba(0,0,0,0.8); color: white; font-size: 10px; font-weight: bold; padding: 1px 4px; border-radius: 2px; font-family: sans-serif;">${callsign.toUpperCase()}</div>` : ''}
    </div>
  `;
  return L.divIcon({
    html: svgIcon,
    iconSize: [40, 60],
    iconAnchor: [20, 20],
    className: "aircraft-marker"
  });
};

// Component to fit bounds on first load only
const MapBoundsHandler = ({ aircraft, hasInitialized, setHasInitialized }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!hasInitialized && aircraft.length > 0) {
      const foundAircraft = aircraft.filter(ac => ac.found && ac.latitude && ac.longitude);
      if (foundAircraft.length > 0) {
        const bounds = L.latLngBounds(
          foundAircraft.map(ac => [ac.latitude, ac.longitude])
        );
        // Add padding to bounds
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
        setHasInitialized(true);
      }
    }
  }, [aircraft, hasInitialized, setHasInitialized, map]);
  
  return null;
};

// Component to handle map resize when sidebar collapses/expands
const MapResizeHandler = ({ sidebarCollapsed }) => {
  const map = useMap();
  
  useEffect(() => {
    // Small delay to allow CSS transition to complete
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 350); // Match the sidebar transition duration (300ms) + buffer
    
    return () => clearTimeout(timer);
  }, [sidebarCollapsed, map]);
  
  return null;
};

// Aircraft info panel component
const AircraftInfoPanel = ({ aircraft, isExpanded, onToggle, onVideoClick, hasVideo, theme = 'dark' }) => {
  if (!aircraft) return null;
  
  const formatAltitude = (alt) => {
    if (alt === null || alt === undefined) return "N/A";
    if (alt === 0) return "Ground";
    return `${alt.toLocaleString()} ft`;
  };
  
  const formatSpeed = (speed) => {
    if (speed === null || speed === undefined) return "N/A";
    return `${Math.round(speed)} kts`;
  };
  
  const formatVerticalRate = (rate) => {
    if (rate === null || rate === undefined) return "N/A";
    const prefix = rate > 0 ? "+" : "";
    return `${prefix}${rate} fpm`;
  };
  
  return (
    <Card className={`${theme === 'dark' ? 'bg-slate-800/95 border-slate-700' : 'bg-white border-slate-300'} backdrop-blur transition-all duration-300 w-full`}>
      <CardHeader className="pb-2 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full animate-pulse" 
              style={{ backgroundColor: aircraft.found ? aircraft.color : '#666' }}
            />
            <CardTitle className={`text-lg ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{aircraft.callsign}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {hasVideo && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onVideoClick(aircraft);
                }}
                className={`p-1.5 ${aircraft.found ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-600 hover:bg-slate-500'} rounded text-white flex items-center gap-1 text-xs`}
                title="View Live Stream"
              >
                <Video className="w-3 h-3" />
                <span>{aircraft.found ? 'LIVE' : 'VIDEO'}</span>
              </button>
            )}
            <Badge variant={aircraft.found ? "default" : "secondary"} className={aircraft.found ? "bg-green-600" : "bg-slate-600"}>
              {aircraft.found ? "LIVE" : "OFFLINE"}
            </Badge>
          </div>
        </div>
        <div className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{aircraft.registration}</div>
      </CardHeader>
      
      {isExpanded && aircraft.found && (
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Mountain className="w-4 h-4 text-blue-400" />
              <div>
                <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Altitude</div>
                <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{formatAltitude(aircraft.altitude)}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Gauge className="w-4 h-4 text-green-400" />
              <div>
                <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Speed</div>
                <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{formatSpeed(aircraft.ground_speed)}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Navigation className="w-4 h-4 text-amber-400" />
              <div>
                <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Heading</div>
                <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{aircraft.track ? `${Math.round(aircraft.track)}°` : "N/A"}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {aircraft.vertical_rate > 0 ? (
                <ChevronUp className="w-4 h-4 text-green-400" />
              ) : aircraft.vertical_rate < 0 ? (
                <ChevronDown className="w-4 h-4 text-red-400" />
              ) : (
                <div className="w-4 h-4 text-slate-400">—</div>
              )}
              <div>
                <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>V/S</div>
                <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{formatVerticalRate(aircraft.vertical_rate)}</div>
              </div>
            </div>
          </div>
          
          <div className={`border-t pt-2 ${theme === 'dark' ? 'border-slate-700' : 'border-slate-300'}`}>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Radio className="w-4 h-4 text-purple-400" />
                <div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Squawk</div>
                  <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{aircraft.squawk || "N/A"}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Last Seen</div>
                  <div className={`font-mono ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{aircraft.last_seen !== null ? `${aircraft.last_seen}s ago` : "N/A"}</div>
                </div>
              </div>
            </div>
          </div>
          
          {aircraft.latitude && aircraft.longitude && (
            <div className={`border-t pt-2 ${theme === 'dark' ? 'border-slate-700' : 'border-slate-300'}`}>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-red-400" />
                <div className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>
                  {aircraft.latitude.toFixed(5)}, {aircraft.longitude.toFixed(5)}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Draggable/Resizable Video Modal with PIP support
const VideoModal = ({ id, aircraft, videoUrl, onClose, initialPosition, checkConnection }) => {
  const [position, setPosition] = useState(initialPosition || { x: 100, y: 100 });
  const [size, setSize] = useState({ width: 480, height: 320 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [streamStatus, setStreamStatus] = useState('loading'); // 'loading', 'playing', 'error'
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const iframeRef = useRef(null);
  const pipCheckInterval = useRef(null);
  const wasPipActive = useRef(false);
  const streamTimeoutRef = useRef(null);

  // Check connection on mount
  useEffect(() => {
    const verifyConnection = async () => {
      if (checkConnection) {
        const result = await checkConnection();
        if (!result.connected) {
          setStreamStatus('error');
          return;
        }
      }
      
      // Set timeout for stream loading as fallback
      streamTimeoutRef.current = setTimeout(() => {
        setStreamStatus(prev => prev === 'loading' ? 'error' : prev);
      }, 15000);
    };
    
    verifyConnection();

    return () => {
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
      }
    };
  }, [checkConnection]);

  // Handle iframe load event - iframe loads but doesn't mean stream is accessible
  const handleIframeLoad = () => {
    console.log('Iframe loaded, waiting for stream verification...');
  };

  // Handle iframe error - explicit failure
  const handleIframeError = () => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }
    setStreamStatus('error');
  };

  // Retry connection
  const retryConnection = async () => {
    setStreamStatus('loading');
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }
    
    // Check connection first
    if (checkConnection) {
      const result = await checkConnection(true); // Force refresh
      if (!result.connected) {
        setStreamStatus('error');
        return;
      }
    }
    
    streamTimeoutRef.current = setTimeout(() => {
      setStreamStatus(prev => prev === 'loading' ? 'error' : prev);
    }, 15000);
    
    // Force iframe reload
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = '';
      setTimeout(() => {
        if (iframeRef.current) iframeRef.current.src = currentSrc;
      }, 100);
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('.modal-btn')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
  };

  // Restore from minimized state
  const restoreModal = () => {
    setIsMinimized(false);
  };

  // Monitor for PIP state changes using polling
  useEffect(() => {
    const checkPipState = () => {
      const pipElement = document.pictureInPictureElement;
      const isPipNowActive = !!pipElement;
      
      // Detect PIP state change
      if (isPipNowActive && !wasPipActive.current) {
        // PIP just became active - minimize the modal
        setIsMinimized(true);
        wasPipActive.current = true;
      } else if (!isPipNowActive && wasPipActive.current) {
        // PIP just became inactive - restore the modal
        setIsMinimized(false);
        wasPipActive.current = false;
      }
    };

    // Start polling for PIP state
    pipCheckInterval.current = setInterval(checkPipState, 200);

    // Also listen for PIP events on the document
    const handleEnterPip = () => {
      setIsMinimized(true);
      wasPipActive.current = true;
    };

    const handleLeavePip = () => {
      setIsMinimized(false);
      wasPipActive.current = false;
    };

    document.addEventListener('enterpictureinpicture', handleEnterPip, true);
    document.addEventListener('leavepictureinpicture', handleLeavePip, true);

    return () => {
      if (pipCheckInterval.current) {
        clearInterval(pipCheckInterval.current);
      }
      document.removeEventListener('enterpictureinpicture', handleEnterPip, true);
      document.removeEventListener('leavepictureinpicture', handleLeavePip, true);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        e.preventDefault();
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }
      if (isResizing && modalRef.current) {
        e.preventDefault();
        const rect = modalRef.current.getBoundingClientRect();
        setSize({
          width: Math.max(320, e.clientX - rect.left),
          height: Math.max(240, e.clientY - rect.top)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = isDragging ? 'move' : 'se-resize';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset]);

  // Render both states but keep iframe always mounted to preserve PIP
  return (
    <>
      {/* Hidden iframe container - keeps PIP alive when minimized */}
      <div
        ref={modalRef}
        className={`fixed z-[2000] bg-slate-900 border border-slate-600 rounded-lg shadow-2xl overflow-hidden select-none transition-opacity duration-200 ${isMinimized ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          visibility: isMinimized ? 'hidden' : 'visible'
        }}
      >
        {/* Header - Draggable */}
        <div
          className="flex items-center justify-between px-3 py-2 bg-slate-800 cursor-move select-none"
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-red-500" />
            <span className="text-white text-sm font-medium">{aircraft.callsign} - Live</span>
            <span className="px-1.5 py-0.5 bg-red-600 text-white text-xs rounded animate-pulse">LIVE</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="modal-btn p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
              title="Minimize window"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="modal-btn p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Video iframe container - always mounted */}
        <div className="relative bg-slate-950" style={{ height: size.height - 40 }}>
          {streamStatus === 'error' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
              <div className="mb-4">
                <Shield size={64} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">SECURED CONNECTION REQUIRED</h3>
              <div className="w-16 h-0.5 bg-red-500 mb-3"></div>
              <p className="text-red-500 font-bold text-xl mb-3">ACCESS DENIED</p>
              <p className="text-slate-400 text-sm mb-4">
                You must be connected to the <strong className="text-white">NSAS VPN</strong><br/>
                or be physically present at <strong className="text-white">NSAS Headquarters</strong><br/>
                to access this live stream.
              </p>
              <button 
                onClick={retryConnection}
                className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded transition-colors"
              >
                <RefreshCw size={16} />
                Retry Connection
              </button>
            </div>
          ) : (
            <>
              {streamStatus === 'loading' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)' }}>
                  <div className="nsas-loader-3d" style={{ perspective: '400px', width: '120px', height: '60px', position: 'relative' }}>
                    <img 
                      src={NSAS_LOGO} 
                      alt="Loading" 
                      className="nsas-logo-spin"
                      style={{
                        width: '100%',
                        height: 'auto',
                        animation: 'spin3d 3s ease-in-out infinite',
                        filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 16px rgba(59, 130, 246, 0.9)) drop-shadow(0 0 32px rgba(59, 130, 246, 0.6))',
                        transformStyle: 'preserve-3d'
                      }}
                    />
                  </div>
                  <p className="text-slate-400 text-sm mt-4">Connecting to stream...</p>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={videoUrl}
                className="w-full h-full border-0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`Live stream - ${aircraft.callsign}`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            </>
          )}
          {/* Overlay to prevent iframe selection during drag/resize */}
          {(isDragging || isResizing) && (
            <div className="absolute inset-0 bg-transparent" />
          )}
        </div>
        
        {/* Resize handle */}
        <div
          className="resize-handle absolute bottom-0 right-0 w-5 h-5 cursor-se-resize"
          onMouseDown={handleResizeStart}
          style={{
            background: 'linear-gradient(135deg, transparent 50%, #64748b 50%)'
          }}
        />
      </div>

      {/* Minimized bar - shown when minimized */}
      {isMinimized && (
        <div
          className="fixed z-[2001] bg-slate-800 border border-slate-600 rounded-lg shadow-xl cursor-pointer hover:bg-slate-700 transition-colors"
          style={{ left: position.x, top: position.y, width: 220 }}
          onClick={restoreModal}
          title="Click to restore video window"
        >
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <Video className="w-3 h-3 text-red-500" />
              <span className="text-white text-xs font-medium truncate">{aircraft.callsign}</span>
              <span className="px-1.5 py-0.5 bg-amber-600 text-white text-[10px] rounded">MINIMIZED</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); restoreModal(); }}
                className="modal-btn p-1 hover:bg-slate-600 rounded text-slate-400 hover:text-white"
                title="Restore"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="modal-btn p-1 hover:bg-slate-600 rounded text-slate-400 hover:text-white"
                title="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Format Zulu time - matching Ops Dashboard style
const formatZuluTime = () => {
  const now = new Date();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayName = days[now.getUTCDay()];
  const day = now.getUTCDate().toString().padStart(2, '0');
  const month = months[now.getUTCMonth()];
  const year = now.getUTCFullYear();
  const hours = now.getUTCHours().toString().padStart(2, '0');
  const minutes = now.getUTCMinutes().toString().padStart(2, '0');
  const seconds = now.getUTCSeconds().toString().padStart(2, '0');
  return {
    date: `${dayName}, ${day} ${month} ${year}`,
    time: `${hours}:${minutes}:${seconds} UTC`
  };
};

export default function AircraftTracker() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [aircraft, setAircraft] = useState([]);
  const [ftxDesignators, setFtxDesignators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const [expandedPanels, setExpandedPanels] = useState({});
  const [trackHistory, setTrackHistory] = useState({});
  const [historyHours, setHistoryHours] = useState(4);
  const [showTrackSettings, setShowTrackSettings] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [videoModals, setVideoModals] = useState([]); // Support multiple video modals
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [zuluTime, setZuluTime] = useState(formatZuluTime());
  const intervalRef = useRef(null);
  const videoModalCounter = useRef(0); // For unique IDs
  
  // Use shared media server connection hook (don't auto-check, check on demand when opening video)
  const { checkConnection } = useMediaServerConnection(false);
  
  // Update Zulu clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setZuluTime(formatZuluTime());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);
  
  // Fetch FTX designators to get video URLs
  const fetchFtxDesignators = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/config`);
      if (response.ok) {
        const data = await response.json();
        setFtxDesignators(data.ftxDesignators || []);
      }
    } catch (err) {
      console.error("Error fetching FTX designators:", err);
    }
  }, []);
  
  useEffect(() => {
    fetchFtxDesignators();
  }, [fetchFtxDesignators]);
  
  // Get video URL for an aircraft by matching callsign/registration with FTX designators
  const getVideoUrl = useCallback((ac) => {
    for (const ftx of ftxDesignators) {
      if (typeof ftx === 'object' && ftx.videoStreamUrl) {
        const ftxName = (ftx.name || '').toUpperCase();
        const acCallsign = (ac.callsign || '').toUpperCase();
        const acReg = (ac.registration || '').toUpperCase();
        const ftxIcao = (ftx.adsbIcaoHex || '').toLowerCase();
        
        // Match by name, ICAO hex, or registration
        if (ftxName === acCallsign || 
            ftxName.includes(acCallsign) || 
            acCallsign.includes(ftxName) ||
            ftxIcao === ac.icao_hex ||
            acReg === (ftx.registration || '').toUpperCase()) {
          return ftx.videoStreamUrl;
        }
      }
    }
    return null;
  }, [ftxDesignators]);
  
  // State to track if we've already fetched the initial trace
  const [initialTraceLoaded, setInitialTraceLoaded] = useState(false);
  
  const fetchAircraftData = useCallback(async () => {
    try {
      // On first load, try to fetch trace data from ADS-B Exchange for full flight path
      const fetchTrace = !initialTraceLoaded;
      const response = await fetch(`${API_URL}/api/aircraft/track-with-history?hours=${historyHours}&fetch_trace=${fetchTrace}`);
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      const data = await response.json();
      setAircraft(data.aircraft || []);
      setLastUpdate(new Date());
      setError(null);
      
      if (fetchTrace) {
        setInitialTraceLoaded(true);
      }
      
      // Update track history from server response
      const newHistory = {};
      data.aircraft.forEach(ac => {
        if (ac.track_history && ac.track_history.length > 0) {
          // Filter out any points with invalid coordinates
          const validPoints = ac.track_history
            .filter(point => 
              point.latitude != null && 
              point.longitude != null && 
              !isNaN(point.latitude) && 
              !isNaN(point.longitude)
            )
            .map(point => [point.latitude, point.longitude]);
          
          if (validPoints.length > 0) {
            newHistory[ac.icao_hex] = validPoints;
          }
        }
      });
      setTrackHistory(newHistory);
      
    } catch (err) {
      console.error("Error fetching aircraft data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [historyHours, initialTraceLoaded]);
  
  useEffect(() => {
    fetchAircraftData();
    
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchAircraftData, refreshInterval * 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchAircraftData, autoRefresh, refreshInterval]);
  
  const handleAircraftClick = (ac) => {
    setExpandedPanels(prev => ({ ...prev, [ac.icao_hex]: true }));
  };
  
  const togglePanel = (icaoHex) => {
    setExpandedPanels(prev => ({ ...prev, [icaoHex]: !prev[icaoHex] }));
  };
  
  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => !prev);
  };
  
  // Open a new video modal (supports multiple)
  const openVideoModal = (ac) => {
    const videoUrl = getVideoUrl(ac);
    if (videoUrl) {
      // Check if this aircraft already has a modal open
      const existingModal = videoModals.find(m => m.aircraft.icao_hex === ac.icao_hex);
      if (existingModal) {
        // Don't open duplicate, maybe bring existing to front
        return;
      }
      
      // Calculate position offset based on number of open modals
      const offset = videoModals.length * 30;
      videoModalCounter.current += 1;
      
      setVideoModals(prev => [...prev, {
        id: videoModalCounter.current,
        aircraft: ac,
        url: videoUrl,
        position: { x: 100 + offset, y: 100 + offset }
      }]);
    }
  };
  
  // Close a specific video modal
  const closeVideoModal = (modalId) => {
    setVideoModals(prev => prev.filter(m => m.id !== modalId));
  };
  
  const foundAircraft = aircraft.filter(ac => ac.found);
  
  return (
    <div className={`min-h-screen text-white ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'}`}>
      {/* Header - styled like Ops Dashboard but compact */}
      <div className={`fixed top-0 left-0 right-0 z-[1000] border-b ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}`}>
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left: Logo */}
          <div className="flex items-center" style={{ minWidth: '120px' }}>
            <img 
              src={NSAS_LOGO} 
              alt="NSAS" 
              style={{ 
                height: '36px', 
                width: 'auto', 
                cursor: 'pointer',
                filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }}
              onClick={() => navigate("/")}
              title="Back to Home"
            />
          </div>
          
          {/* Center: Zulu Clock - JetBrains Mono style like Ops Dashboard */}
          <div className="flex flex-col items-center" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <span className={`text-xs ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>{zuluTime.date}</span>
            <span className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{zuluTime.time}</span>
          </div>
          
          {/* Right: Title and Controls */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end">
              <span 
                className={`text-base font-bold tracking-wider uppercase ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}
                style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.1em' }}
              >
                Live Aircraft Tracker
              </span>
              <div className="flex items-center gap-2 text-xs">
                <div className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-slate-500'}`} />
                <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>
                  {autoRefresh ? `${refreshInterval}s` : "Paused"}
                </span>
                {lastUpdate && (
                  <span className={theme === 'dark' ? 'text-slate-500' : 'text-slate-500'}>
                    {lastUpdate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
            
            {/* Track History Settings */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTrackSettings(!showTrackSettings)}
                className={`${theme === 'dark' ? 'border-slate-600' : 'border-slate-300'} text-amber-500 hover:text-amber-400`}
              >
                <Navigation className="w-4 h-4 mr-2" />
                Track: {historyHours}h
              </Button>
              
              {showTrackSettings && (
                <div className={`absolute right-0 top-10 ${theme === 'dark' ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'} border rounded-lg p-3 shadow-xl z-50 w-48`}>
                  <div className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'} mb-2`}>Track History</div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 4, 8, 12, 24].map(h => (
                      <button
                        key={h}
                        onClick={() => { setHistoryHours(h); setShowTrackSettings(false); }}
                        className={`px-2 py-1 text-xs rounded ${historyHours === h 
                          ? 'bg-amber-500 text-black' 
                          : theme === 'dark' 
                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                      >
                        {h}h
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAutoRefresh}
              className={`${theme === 'dark' ? 'border-slate-600' : 'border-slate-300'} ${autoRefresh ? 'text-green-500' : theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {autoRefresh ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAircraftData}
              disabled={loading}
              className={`${theme === 'dark' ? 'border-slate-600 text-slate-400 hover:text-white' : 'border-slate-300 text-slate-600 hover:text-slate-900'}`}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Theme Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className={`${theme === 'dark' ? 'border-slate-600 text-slate-400 hover:text-white' : 'border-slate-300 text-slate-600 hover:text-slate-900'}`}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="pt-14 h-screen flex">
        {/* Sidebar - Aircraft List (Collapsible) */}
        <div 
          className={`${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-slate-300'} border-r overflow-hidden transition-all duration-300 flex flex-col ${
            sidebarCollapsed ? 'w-12' : 'w-96'
          }`}
        >
          {!sidebarCollapsed && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Tracked Aircraft</h2>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`${theme === 'dark' ? 'border-slate-600 text-white' : 'border-slate-400 text-slate-800'}`}>
                    {foundAircraft.length}/{aircraft.length} Online
                  </Badge>
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    className={`p-1.5 rounded transition-colors ${theme === 'dark' ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'}`}
                    title="Collapse panel"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              {loading && aircraft.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Sort aircraft: live first, then offline */}
                  {[...aircraft].sort((a, b) => {
                    if (a.found && !b.found) return -1;
                    if (!a.found && b.found) return 1;
                    return 0;
                  }).map(ac => (
                    <AircraftInfoPanel
                      key={ac.icao_hex}
                      aircraft={ac}
                      isExpanded={expandedPanels[ac.icao_hex]}
                      onToggle={() => togglePanel(ac.icao_hex)}
                      onVideoClick={openVideoModal}
                      hasVideo={!!getVideoUrl(ac)}
                      theme={theme}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Collapsed state - just show icons */}
          {sidebarCollapsed && (
            <div className="flex flex-col items-center py-4 gap-2">
              <button
                onClick={() => setSidebarCollapsed(false)}
                className={`p-1.5 rounded transition-colors mb-2 ${theme === 'dark' ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'}`}
                title="Expand panel"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className={`text-xs mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                {foundAircraft.length}/{aircraft.length}
              </div>
              {[...aircraft].sort((a, b) => {
                if (a.found && !b.found) return -1;
                if (!a.found && b.found) return 1;
                return 0;
              }).map(ac => (
                <div
                  key={ac.icao_hex}
                  className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                  style={{ backgroundColor: ac.found ? ac.color : '#475569' }}
                  title={`${ac.callsign} - ${ac.found ? 'LIVE' : 'OFFLINE'}`}
                  onClick={() => {
                    setSidebarCollapsed(false);
                    setExpandedPanels(prev => ({ ...prev, [ac.icao_hex]: true }));
                  }}
                >
                  <Plane className="w-4 h-4 text-white" style={{ transform: `rotate(${ac.track || 0}deg)` }} />
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={[36.6, -4.5]}
            zoom={6}
            style={{ height: "100%", width: "100%" }}
            className="z-0"
          >
            {/* Use theme-appropriate map tiles */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={theme === 'dark' 
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              }
              maxZoom={20}
              subdomains="abcd"
            />
            
            {/* Fit bounds on first load only */}
            <MapBoundsHandler 
              aircraft={aircraft} 
              hasInitialized={hasInitialized} 
              setHasInitialized={setHasInitialized} 
            />
            
            {/* Handle map resize when sidebar collapses/expands */}
            <MapResizeHandler sidebarCollapsed={sidebarCollapsed} />
            
            {/* Track history lines - only for online aircraft */}
            {Object.entries(trackHistory).map(([icaoHex, positions]) => {
              if (!positions || positions.length < 2) return null;
              const ac = aircraft.find(a => a.icao_hex === icaoHex);
              // Only show track for online aircraft
              if (!ac?.found) return null;
              // Ensure positions are valid numbers
              const validPositions = positions.filter(pos => 
                Array.isArray(pos) && 
                pos.length === 2 && 
                typeof pos[0] === 'number' && 
                typeof pos[1] === 'number' &&
                !isNaN(pos[0]) && 
                !isNaN(pos[1])
              );
              if (validPositions.length < 2) return null;
              return (
                <Polyline
                  key={`track-${icaoHex}`}
                  positions={validPositions}
                  pathOptions={{ 
                    color: ac?.color || "#FF6B00", 
                    weight: 4, 
                    opacity: 1 
                  }}
                />
              );
            })}
            
            {/* Aircraft markers */}
            {foundAircraft.map(ac => (
              ac.latitude && ac.longitude && (
                <Marker
                  key={ac.icao_hex}
                  position={[ac.latitude, ac.longitude]}
                  icon={createAircraftIcon(ac.color, ac.track || 0, ac.callsign)}
                  eventHandlers={{
                    click: () => handleAircraftClick(ac)
                  }}
                >
                  <Popup className="aircraft-popup">
                    <div className="text-center">
                      <div className="font-bold text-lg">{ac.callsign}</div>
                      <div className="text-sm text-gray-600">{ac.registration}</div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Alt:</span>{" "}
                          {ac.altitude ? `${ac.altitude.toLocaleString()} ft` : "N/A"}
                        </div>
                        <div>
                          <span className="text-gray-500">Spd:</span>{" "}
                          {ac.ground_speed ? `${Math.round(ac.ground_speed)} kts` : "N/A"}
                        </div>
                        <div>
                          <span className="text-gray-500">Hdg:</span>{" "}
                          {ac.track ? `${Math.round(ac.track)}°` : "N/A"}
                        </div>
                        <div>
                          <span className="text-gray-500">Sqk:</span>{" "}
                          {ac.squawk || "N/A"}
                        </div>
                      </div>
                      {getVideoUrl(ac) && (
                        <button
                          onClick={() => openVideoModal(ac)}
                          className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded flex items-center gap-1 mx-auto"
                        >
                          <Video className="w-3 h-3" />
                          Watch Live
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>
      </div>
      
      {/* Video Modals - Multiple supported */}
      {videoModals.map(modal => (
        <VideoModal
          key={modal.id}
          id={modal.id}
          aircraft={modal.aircraft}
          videoUrl={modal.url}
          initialPosition={modal.position}
          onClose={() => closeVideoModal(modal.id)}
          checkConnection={checkConnection}
        />
      ))}
    </div>
  );
}
